import { useState } from "react";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import { Link } from "react-router-dom";
import { ArrowLeft, BarChart3, TrendingUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function InteresseComposto() {
  const [capital, setCapital] = useState("");
  const [monthlyReturn, setMonthlyReturn] = useState("");
  const [months, setMonths] = useState("12");
  const [deposit, setDeposit] = useState("");
  const [depositFreq, setDepositFreq] = useState("monthly");
  const [result, setResult] = useState(null);

  const calculate = () => {
    const cap = parseFloat(capital);
    const rate = parseFloat(monthlyReturn) / 100;
    const m = parseInt(months);
    const dep = parseFloat(deposit) || 0;
    if (!cap || !rate || !m) return;

    const timeline = [];
    let current = cap;
    let totalDeposits = 0;
    for (let i = 1; i <= m; i++) {
      const profit = current * rate;
      current += profit;
      let deposited = 0;
      if (dep > 0 && (depositFreq === "monthly" || (depositFreq === "yearly" && i % 12 === 0))) {
        current += dep;
        totalDeposits += dep;
        deposited = dep;
      }
      timeline.push({ month: i, balance: current.toFixed(2), profit: profit.toFixed(2), deposited });
    }

    const invested = cap + totalDeposits;
    setResult({
      finalBalance: current.toFixed(2),
      totalProfit: (current - invested).toFixed(2),
      totalDeposits: totalDeposits.toFixed(2),
      totalReturn: (((current - invested) / invested) * 100).toFixed(1),
      hasDeposits: totalDeposits > 0,
      timeline
    });
  };

  return (
    <div className="page-container" data-testid="interesse-composto-page">
      <PageHero image={HERO_IMAGES.cta} kicker="Compound Interest" title="Interesse Composto" subtitle="Simula la crescita del tuo capitale nel tempo" />
      <SectionBg type="crypto" />
      <div className="container py-8 relative">
        <Link to="/strumenti" className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-lg transition-all hover:bg-white/5" style={{ color: "var(--mm-gold)" }} data-testid="back-to-strumenti">
          <ArrowLeft size={20} /><span style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Torna agli Strumenti</span>
        </Link>

        <div className="max-w-2xl mx-auto">
          <div className="glass-card p-6 sm:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="text-sm font-semibold mb-2 block" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>CAPITALE INIZIALE ($)</label>
                <input type="number" value={capital} onChange={(e) => setCapital(e.target.value)} className="admin-input" placeholder="10000" data-testid="ci-capital" />
              </div>
              <div>
                <label className="text-sm font-semibold mb-2 block" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>RENDIMENTO MENSILE (%)</label>
                <input type="number" step="any" value={monthlyReturn} onChange={(e) => setMonthlyReturn(e.target.value)} className="admin-input" placeholder="5" data-testid="ci-return" />
              </div>
              <div>
                <label className="text-sm font-semibold mb-2 block" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>MESI</label>
                <input type="number" value={months} onChange={(e) => setMonths(e.target.value)} className="admin-input" placeholder="12" data-testid="ci-months" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-sm font-semibold mb-2 block" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>DEPOSITO RICORRENTE ($) <span style={{ color: "var(--mm-text-muted)", fontWeight: 400 }}>— opzionale</span></label>
                <input type="number" step="any" value={deposit} onChange={(e) => setDeposit(e.target.value)} className="admin-input" placeholder="0" data-testid="ci-deposit" />
              </div>
              <div>
                <label className="text-sm font-semibold mb-2 block" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>FREQUENZA DEPOSITO</label>
                <select value={depositFreq} onChange={(e) => setDepositFreq(e.target.value)} className="admin-input" data-testid="ci-deposit-freq">
                  <option value="monthly">Mensile</option>
                  <option value="yearly">Annuale</option>
                </select>
              </div>
            </div>
            <button onClick={calculate} className="w-full py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-[1.02]" style={{ backgroundColor: "var(--mm-gold)", color: "#000", fontFamily: "'Space Grotesk', sans-serif" }} data-testid="ci-calc-btn">CALCOLA CRESCITA</button>
          </div>

          {result && (
            <div className="mt-6 space-y-4 animate-fadeIn" data-testid="ci-results">
              {/* Summary */}
              <div className={`grid gap-3 ${result.hasDeposits ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}>
                <div className="glass-card p-4 text-center" style={{ borderColor: "rgba(47, 216, 168, 0.2)" }}>
                  <p className="text-xs mb-1" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>CAPITALE FINALE</p>
                  <p className="text-xl sm:text-2xl font-bold gold-text" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>${Number(result.finalBalance).toLocaleString()}</p>
                </div>
                {result.hasDeposits && (
                  <div className="glass-card p-4 text-center" style={{ borderColor: "rgba(255, 255, 255, 0.1)" }}>
                    <p className="text-xs mb-1" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>DEPOSITI TOTALI</p>
                    <p className="text-xl sm:text-2xl font-bold" style={{ color: "var(--mm-text-primary)", fontFamily: "'Space Grotesk', sans-serif" }} data-testid="ci-total-deposits">${Number(result.totalDeposits).toLocaleString()}</p>
                  </div>
                )}
                <div className="glass-card p-4 text-center" style={{ borderColor: "rgba(16, 185, 129, 0.2)" }}>
                  <p className="text-xs mb-1" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>PROFITTO TOTALE</p>
                  <p className="text-xl sm:text-2xl font-bold" style={{ color: "#10B981", fontFamily: "'Space Grotesk', sans-serif" }}>+${Number(result.totalProfit).toLocaleString()}</p>
                </div>
                <div className="glass-card p-4 text-center" style={{ borderColor: "rgba(100, 200, 255, 0.2)" }}>
                  <p className="text-xs mb-1" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>RENDIMENTO</p>
                  <p className="text-xl sm:text-2xl font-bold" style={{ color: "#64C8FF", fontFamily: "'Space Grotesk', sans-serif" }}>{result.totalReturn}%</p>
                </div>
              </div>

              {/* Grafico crescita */}
              <div className="glass-card p-4 sm:p-6" data-testid="ci-chart">
                <h3 className="text-base font-semibold mb-4" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>CURVA DI CRESCITA</h3>
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <AreaChart data={result.timeline.map(r => ({ mese: r.month, balance: Number(r.balance) }))} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gvGrowth" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#2FD8A8" stopOpacity={0.45} />
                          <stop offset="100%" stopColor="#2FD8A8" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                      <XAxis dataKey="mese" tick={{ fill: "#8A8F98", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
                      <YAxis tick={{ fill: "#8A8F98", fontSize: 11 }} tickLine={false} axisLine={false} width={70} tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(v >= 100000 ? 0 : 1)}k` : v}`} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "#0F1512", border: "1px solid rgba(47,216,168,0.35)", borderRadius: 8, fontFamily: "'Space Grotesk', sans-serif" }}
                        labelStyle={{ color: "#8A8F98" }}
                        formatter={(v) => [`$${Number(v).toLocaleString()}`, "Balance"]}
                        labelFormatter={(l) => `Mese ${l}`}
                      />
                      <Area type="monotone" dataKey="balance" stroke="#2FD8A8" strokeWidth={2.5} fill="url(#gvGrowth)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Timeline */}
              <div className="glass-card p-4 sm:p-6 overflow-x-auto">
                <h3 className="text-base font-semibold mb-4" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>PROIEZIONE MENSILE</h3>
                <table className="w-full text-sm" style={{ minWidth: "400px" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--mm-border)" }}>
                      <th className="text-left py-2 px-2" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600 }}>Mese</th>
                      <th className="text-right py-2 px-2" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600 }}>Balance</th>
                      {result.hasDeposits && <th className="text-right py-2 px-2" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600 }}>Deposito</th>}
                      <th className="text-right py-2 px-2" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600 }}>Profitto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.timeline.map((row) => (
                      <tr key={row.month} style={{ borderBottom: "1px solid var(--mm-border)" }}>
                        <td className="py-2 px-2" style={{ color: "var(--mm-text-primary)", fontFamily: "'Space Grotesk', sans-serif" }}>{row.month}</td>
                        <td className="py-2 px-2 text-right font-semibold" style={{ color: "var(--mm-text-primary)", fontFamily: "'Space Grotesk', sans-serif" }}>${Number(row.balance).toLocaleString()}</td>
                        {result.hasDeposits && <td className="py-2 px-2 text-right" style={{ color: row.deposited ? "var(--mm-gold)" : "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>{row.deposited ? `+$${Number(row.deposited).toLocaleString()}` : "—"}</td>}
                        <td className="py-2 px-2 text-right" style={{ color: "#10B981", fontFamily: "'Space Grotesk', sans-serif" }}>+${Number(row.profit).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
